import { Button, Label, TargetIcon, Typography } from 'procyon-ui'
import React, { useState, useMemo } from 'react'
import { useChannelBrowser } from '../../../providers/hooks'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { SelectChannel } from '../../Modal'
import { enqueueNotification } from 'Utils/Helpers'
import { createRsrcKey } from 'features/resources'
import { getFriendlyName } from 'Utils/FriendlyName'
import { useUser } from 'Core/Hooks/useUser'
import { getHTMLDataTestID } from 'infra/utils'
import { reduxApiClient } from 'infra'

const ServiceNowNotificationChannel = () => {
  const { refresh } = useChannelBrowser()
  const { slices } = useMultiSlice(['notificationSubscriptions', 'servicenowtables'])
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notificationType, setNotificationType] = useState('')
  const { isAgentlessMode } = useUser()
  const [activeChannel, setActiveChannel] = useState('')

  const privateServerChannel = useMemo(() => {
    const privateNS = slices.notificationSubscriptions.find((e) => {
      const { SubscriptionFor, Subscriber } = e.Spec
      return (
        SubscriptionFor.RefID === '0' &&
        SubscriptionFor.RefKind === 'Server' &&
        Subscriber.RefKind === 'ServiceNowTable'
      )
    })

    if (privateNS) {
      const { Subscriber } = privateNS.Spec
      return slices.servicenowtables.find((e) => e.ObjectMeta.ID === Subscriber.RefID)
    }
  }, [slices])

  const ApplicationTaskTable = useMemo(() => {
    const appNS = slices.notificationSubscriptions.find((e) => {
      const { SubscriptionFor } = e.Spec
      return SubscriptionFor.RefID === '0' && SubscriptionFor.RefKind === 'Application'
    })

    if (appNS) {
      const { Subscriber } = appNS.Spec
      return slices.servicenowtables.find((e) => e.ObjectMeta.ID === Subscriber.RefID)
    }
  }, [slices])

  const getApplicationNotificationIcon = () => {
    return ApplicationTaskTable ? <TargetIcon type='SERVICENOW' /> : ''
  }

  const getApplicationNotificationVariant = () => {
    return ApplicationTaskTable ? 'grayBlue' : 'gray'
  }

  const getApplicationChannelName = () => {
    return (
      <>
        {ApplicationTaskTable ? (
          <strong>{ApplicationTaskTable.Spec.Name}</strong>
        ) : (
          <em>No table selected yet</em>
        )}
      </>
    )
  }

  const getPrivateNotificationIcon = () => {
    return privateServerChannel ? <TargetIcon type='SERVICENOW' /> : ''
  }

  const getPrivateNotificationVariant = () => {
    return privateServerChannel ? 'grayBlue' : 'gray'
  }

  const getPrivateChannelName = () => {
    return (
      <>
        {privateServerChannel ? (
          <strong>{privateServerChannel.Spec.Name}</strong>
        ) : (
          <em>No table selected yet</em>
        )}
      </>
    )
  }

  const handleModalClick = (type) => {
    setNotificationType(type)
    if (type === 'application') {
      setActiveChannel(ApplicationTaskTable)
    } else {
      setActiveChannel(privateServerChannel)
    }
    setShowModal(true)
  }

  const handleChannelSelection = (channel) => {
    if (activeChannel?.Spec?.Id === channel.Spec.Id) {
      setActiveChannel({})
    } else {
      // Else select
      setActiveChannel(channel)
    }
  }

  const onConfirmChannelChange = (selectedChannel, notificationType) => {
    console.log(selectedChannel, notificationType, '===== notificationType')
    setShowModal(false)
    if (notificationType === 'application') {
      applicationNotificationChannel(selectedChannel)
    } else {
      privateNotificationChannel(selectedChannel)
    }
  }

  const applicationNotificationChannel = async (selectedChannels) => {
    setLoading(true)
    try {
      const appNS = slices.notificationSubscriptions.find(
        (e) => e.Spec.SubscriptionFor.RefKind === 'Application'
      )

      if (!Object.keys(selectedChannels).length) {
        if (appNS) {
          await reduxApiClient('notificationsubscriptions').delete(appNS)
          refresh()
        }
        setLoading(false)
        return
      }

      const selectedChannelRef = {
        RefKind: 'ServiceNowTable',
        RefID: selectedChannels.ObjectMeta.ID
      }

      const appChannel = ApplicationTaskTable
        ? { RefKind: 'ServiceNowTable', RefID: ApplicationTaskTable.ObjectMeta.ID }
        : null
      const channelKey = createRsrcKey(selectedChannelRef)
      if (channelKey === createRsrcKey(appChannel)) {
        setLoading(false)
        return
      }

      if (appNS) {
        await reduxApiClient('notificationsubscriptions').delete(appNS)
      }

      const newNS = {
        ObjectMeta: {
          Name: `PRIVATE_${getFriendlyName()}`
        },
        Spec: {
          SubscriptionFor: {
            RefID: '0',
            RefKind: 'Application'
          },
          Subscriber: selectedChannelRef
        }
      }

      await reduxApiClient('notificationsubscriptions').create(newNS)
      enqueueNotification('Application Task Table changed successfully!', 'info')
      refresh()
    } catch (error) {
      enqueueNotification('Failed to change Notification Channel!', 'error')
      refresh()
    } finally {
      setLoading(false)
    }
  }

  const privateNotificationChannel = async (selectedChannels) => {
    setLoading(true)
    try {
      const privateNS = slices.notificationSubscriptions.find(
        (e) =>
          e.Spec.SubscriptionFor.RefKind === 'Server' &&
          e.Spec.Subscriber.RefKind === 'ServiceNowTable'
      )

      if (!Object.keys(selectedChannels).length) {
        if (privateNS) {
          await reduxApiClient('notificationsubscriptions').delete(privateNS)
          refresh()
        }
        setLoading(false)
        return
      }

      const selectedChannelRef = {
        RefKind: 'ServiceNowTable',
        RefID: selectedChannels.ObjectMeta.ID
      }

      const privateChannel = privateServerChannel
        ? { RefKind: 'ServiceNowTable', RefID: privateServerChannel.ObjectMeta.ID }
        : null
      const channelKey = createRsrcKey(selectedChannelRef)
      if (channelKey === createRsrcKey(privateChannel)) {
        setLoading(false)
        return
      }

      if (privateNS) {
        await reduxApiClient('notificationsubscriptions').delete(privateNS)
      }

      const newNS = {
        ObjectMeta: {
          Name: `PRIVATE_${getFriendlyName()}`
        },
        Spec: {
          SubscriptionFor: {
            RefID: '0',
            RefKind: 'Server'
          },
          Subscriber: selectedChannelRef
        }
      }
      await reduxApiClient('notificationsubscriptions').create(newNS)
      enqueueNotification('Private Server Notification Channel changed successfully!', 'info')
      refresh()
    } catch (error) {
      enqueueNotification('Failed to change Notification Channel!', 'error')
      refresh()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='mt-8'>
      <div className='border-b-2'>
        <Typography variant='h4-medium'>Application Task Label</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            data-testid={getHTMLDataTestID('integration', 'label', 'gateway-channel')}
            size='lg'
            icon={getApplicationNotificationIcon()}
            onClick={function noRefCheck() {}}
            text={getApplicationChannelName()}
            variant={getApplicationNotificationVariant()}
            style={{
              padding: '15px'
            }}
          />
          <Button
            data-testid={getHTMLDataTestID('integration', 'button', 'change-application-task')}
            size='md'
            variant='gray'
            onClick={() => handleModalClick('application')}
            disabled={loading}
          >
            {loading && notificationType === 'application' ? 'Saving...' : 'Change Task Table'}
          </Button>
        </div>
      </div>

      <div className='border-b-2 mt-10'>
        <Typography variant='h4-medium'>Private Server Task Label</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            size='lg'
            icon={getPrivateNotificationIcon()}
            onClick={function noRefCheck() {}}
            text={getPrivateChannelName()}
            variant={getPrivateNotificationVariant()}
            style={{
              padding: '15px'
            }}
          />
          <Button
            id='change-private-task-button'
            size='md'
            variant='gray'
            onClick={() => handleModalClick('private')}
            disabled={loading}
          >
            {loading && notificationType === 'private' ? 'Saving...' : 'Change Task Table'}
          </Button>
        </div>
      </div>

      <SelectChannel
        showModal={showModal}
        activeChannel={activeChannel}
        setShowModal={setShowModal}
        onConfirmChannelChange={onConfirmChannelChange}
        notificationType={notificationType}
        handleChannelSelect={handleChannelSelection}
      />
    </div>
  )
}

export { ServiceNowNotificationChannel }
